import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { database } from '../../../services/firebase';
import { ref, get, update } from 'firebase/database';
import { jobListings } from '../../../data/jobListings';
import ApplicationHistory from '../../applications/ApplicationHistory';
import ResumeChecker from '../../resume/ResumeChecker';
import JobsList from '../../jobs/JobsList';
import { GoogleGenerativeAI } from '@google/generative-ai';
import MockTest from '../../mock-test/MockTest';
import { Outlet } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  auth
} from '../../../services/firebase';
import {
  FaSignOutAlt
} from 'react-icons/fa';
import {
  FaUser,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaTools,
  FaClipboardCheck,
  FaHistory,
  FaRobot,
  FaRegClock,
  FaChartBar,
  FaFileAlt,
  FaBriefcase // Add these three
} from 'react-icons/fa';
import CareerAssistant from './CareerAssistant';


const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mockTestConfig, setMockTestConfig] = useState({ type: '', subject: '' });
  const [currentTest, setCurrentTest] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); 
  const [showMockTest, setShowMockTest] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  // Fix in fetchUserData function
  const fetchUserData = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      navigate('/login');
      return;
    }
  
    try {
      const userRef = ref(database, `users/${userId}`);  // Fixed string interpolation
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        setUserData(snapshot.val());
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const startMockTest = async () => {
    const questions = await generateQuestions(mockTestConfig.type, mockTestConfig.subject);
    setCurrentTest({
      questions,
      answers: new Array(questions.length).fill(null),
      startTime: Date.now(),
    });
    setTimeLeft(600);
    setShowMockTest(true);
    startTimer();
  };

  const startTimer = () => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          submitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Fix in submitTest function
  const submitTest = async () => {
    const score = calculateScore();
    const userId = localStorage.getItem('userId');

    const testHistory = {
      type: mockTestConfig.type,
      subject: mockTestConfig.subject,
      score,
      date: new Date().toISOString()
    };

    try {
      const userRef = ref(database, `users/${userId}/mockTests`);
      const snapshot = await get(userRef);
      const existingTests = snapshot.exists() ? snapshot.val() : [];

      await update(ref(database, `users/${userId}`), {
        mockTests: [...existingTests, testHistory]
      });

      setShowMockTest(false);
      // Add navigation to test results with state
      navigate('/dashboard/user/test-results', { 
        state: { 
          testResults: testHistory, 
          currentTest: currentTest 
        } 
      });
      fetchUserData();
    } catch (error) {
      console.error('Error saving test results:', error);
    }
  };

  const calculateScore = () => {
    if (!currentTest) return 0;
    const correctAnswers = currentTest.answers.filter(
      (answer, index) => answer === currentTest.questions[index].correct
    ).length;
    return Math.round((correctAnswers / currentTest.questions.length) * 100);
  };


  // In renderOverview, remove the handleLogout definition and keep just the JSX
  const renderOverview = () => {
    const stats = [
      {
        title: 'Mock Tests Taken',
        value: userData?.mockTests?.length || 0,
        icon: <FaClipboardCheck className="text-green-500" />,
      },
      {
        title: 'Average Score',
        value: `${(
          userData?.mockTests?.reduce((acc, test) => acc + test.score, 0) /
          (userData?.mockTests?.length || 1)
        ).toFixed(2)}%`,
        icon: <FaHistory className="text-blue-500" />,
      },
    ];

    // Add these imports at the top
    

    // Update the handleLogout function
    const handleLogout = () => {
      localStorage.removeItem('userId');
      navigate('/login');
    };

    // Update the Profile Header section in renderOverview
    return (
      <div className="space-y-8">
        {/* Profile Header */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center">
                <FaUser className="w-10 h-10 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {userData?.personalInfo?.firstName} {userData?.personalInfo?.lastName}
                </h2>
                <p className="text-gray-600">{userData?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg
                           hover:bg-red-600 transition-colors duration-200"
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </div>
        </div>
    
        {/* Update the Profile Sections styling */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 
                         hover:border-indigo-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FaUser className="text-indigo-600" />
                <h3 className="text-lg font-semibold">Personal Information</h3>
              </div>
              <button
                onClick={() => navigate('/profile/personal')}
                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Edit
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <span className="font-medium w-24">Name:</span>
                <span className="text-gray-700">{userData?.fullName || 'Not set'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium w-24">Email:</span>
                <span className="text-gray-700">{userData?.email || 'Not set'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium w-24">Phone:</span>
                <span className="text-gray-700">{userData?.phone || 'Not set'}</span>
              </div>
            </div>
          </div>

          {/* Skills with updated styling */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 
                         hover:border-indigo-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FaTools className="text-indigo-600" />
                <h3 className="text-lg font-semibold">Skills</h3>
              </div>
              <button
                onClick={() => navigate('/profile/skills')}
                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Edit
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {userData?.skills?.length > 0 ? (
                userData.skills.map((skill, index) => (
                  <span key={index} 
                    className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm
                             hover:bg-indigo-200 transition-colors duration-200">
                    {skill}
                  </span>
                ))
              ) : (
                <p className="text-gray-500 italic">No skills added yet</p>
              )}
            </div>
          </div>

          {/* Education with updated styling */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 
                         hover:border-indigo-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FaGraduationCap className="text-indigo-600" />
                <h3 className="text-lg font-semibold">Education</h3>
              </div>
              <button
                onClick={() => navigate('/profile/education')}
                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Edit
              </button>
            </div>
            <div className="space-y-4">
              {userData?.education?.length > 0 ? (
                userData.education.map((edu, index) => (
                  <div key={index} className="border-b pb-3 last:border-0 last:pb-0">
                    <p className="font-medium text-gray-900">{edu.institution}</p>
                    <p className="text-sm text-gray-600">{edu.fieldOfStudy} - {edu.educationLevel}</p>
                    <p className="text-sm text-gray-600">{edu.startYear} - {edu.currentlyStudying ? 'Present' : edu.endYear}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No education details added</p>
              )}
            </div>
          </div>

          {/* Address with updated styling */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 
                         hover:border-indigo-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FaMapMarkerAlt className="text-indigo-600" />
                <h3 className="text-lg font-semibold">Address</h3>
              </div>
              <button
                onClick={() => navigate('/profile/address')}
                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Edit
              </button>
            </div>
            <div className="space-y-2">
              {userData? (
                <>
                  <p className="text-gray-700">Address: {userData.streetAddress}</p>
                  <p className="text-gray-700">Country: {userData.country} - {userData.city}</p>
                  <p className="text-gray-700">Pincode: {userData.pincode}</p>
                </>
              ) : (
                <p className="text-gray-500 italic">No address added yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  const renderResumeChecker = () => (
    <ResumeChecker />
  );
  
  // Replace the renderMockTest function with:
  const renderMockTest = () => (
    <MockTest
      showMockTest={showMockTest}
      mockTestConfig={mockTestConfig}
      setMockTestConfig={setMockTestConfig}
      startMockTest={startMockTest}
      timeLeft={timeLeft}
      currentTest={currentTest}
      setCurrentTest={setCurrentTest}
      submitTest={submitTest}
    />
  );

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: <FaChartBar /> },
    { id: 'resume-checker', label: 'AI Resume Checker', icon: <FaFileAlt /> },
    { id: 'mock-test', label: 'AI Mock Test', icon: <FaRobot /> },
    { id: 'jobs', label: 'Apply for Jobs', icon: <FaBriefcase /> },
    { id: 'applications', label: 'My Applications', icon: <FaClipboardCheck /> },
    { id: 'career-assistant', label: 'Career Assistant', icon: <FaRobot /> }
  ];

  // Add new state for jobs
  const [jobs, setJobs] = useState(jobListings);

  const renderSidebar = () => (
    <div className="w-64 bg-indigo-900 h-screen fixed left-0 top-0 text-white p-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">JobGenius</h1>
      </div>
      <nav>
        {sidebarItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
              activeTab === item.id ? 'bg-indigo-700' : 'hover:bg-indigo-800'
            }`}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
  const renderJobs = () => (
    <JobsList jobs={jobs} onApply={handleApply} />
  );

  const [userApplications, setUserApplications] = useState([]);
  useEffect(() => {
    fetchUserApplications();
  }, []);

  const fetchUserApplications = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;

  try {
    const userRef = ref(database, `users/${userId}/applications`);
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      setUserApplications(snapshot.val());
    } } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };
  
  const renderApplications = () => (
    <ApplicationHistory applications={userApplications} jobs={jobs} />
  );
  // Add this function before renderJobs
  const handleApply = async (jobId) => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      navigate('/login');
      return;
    }

    try {
      const application = {
        jobId,
        userId,
        status: 'pending',
        appliedAt: new Date().toISOString()
      };

      const userRef = ref(database, `users/${userId}/applications`);
      const snapshot = await get(userRef);
      const existingApplications = snapshot.exists() ? snapshot.val() : [];
      
      await update(ref(database, `users/${userId}`), {
        applications: [...existingApplications, application]
      });

      alert('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  // Add this to your return statement
  return (
    <div className="flex">
      {renderSidebar()}
      <div className="ml-64 flex-1 p-8">
        <Outlet />
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'mock-test' && renderMockTest()}
        {activeTab === 'jobs' && renderJobs()}
        {activeTab === 'resume-checker' && renderResumeChecker()}
        {activeTab === 'applications' && renderApplications()}
        {activeTab === 'career-assistant' && <CareerAssistant />}
      </div>
    </div>
  );
};

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

const generateQuestions = async (type, subject) => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-pro' });

    const prompt = `Create a JSON array of 10 multiple choice questions for a ${type} test about ${subject}.
Each question must strictly follow this format:
{
  "text": "question text here",
  "options": ["option1", "option2", "option3", "option4"],
  "correct": 0
}
The correct value must be a number 0-3 representing the index of the correct answer.
Return ONLY the JSON array with no additional text or formatting.`;

    const result = await model.generateContent(prompt);
    const response = result.response;

    let text = await response.text();

    // Clean the response text and remove unwanted characters
    text = text
      .replace(/```json/g, '') // Remove JSON markers
      .replace(/```/g, '') // Remove closing markers
      .replace(/\n/g, '') // Remove new lines
      .trim();

    // Try to find the JSON block correctly if extra text is included
    const jsonMatch = text.match(/\[.*\]/s); // Match JSON array only
    if (!jsonMatch) {
      throw new Error('No valid JSON array found in response.');
    }

    const cleanJson = jsonMatch[0];

    // Parse the clean JSON response
    const questions = JSON.parse(cleanJson);

    // Validate the questions format
    if (!Array.isArray(questions) || questions.length === 0) {
      throw new Error('Invalid questions array');
    }

    // Validate each question
    questions.forEach((q, i) => {
      if (
        !q.text ||
        !Array.isArray(q.options) ||
        q.options.length !== 4 ||
        typeof q.correct !== 'number' ||
        q.correct < 0 ||
        q.correct > 3
      ) {
        throw new Error(`Invalid question format at index ${i}`);
      }
    });

    return questions;
  } catch (error) {
    console.error('Error generating questions:', error);
    throw new Error('Failed to generate questions. Please try again.');
  }
};

export default Dashboard;